import React, { useState, useRef } from 'react'
import { faEnvelope } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  useTheme
} from '@mui/material'
import { toast } from 'react-toastify'
import { useMutation } from '@tanstack/react-query'
import ReCAPTCHA from 'react-google-recaptcha'

import { EmailType, GenericEmailPayload } from '../types/apiTypes'
import { sendEmail } from '../api/email_api'
import CharacterCounter from '../components/CharLimitMessage'
import { fetchEnvironmentVar } from '../utils/miscUtils'
import SEOHelmet from '../components/SEOHelmet'
import { DEFAULT_SEO, generateCanonicalUrl } from '../utils/seo'
import { ROUTES } from '../routes/routes'

const ContactUsPage: React.FC = () => {
  const theme = useTheme()
  const inputCharLimit = 500
  const [emailFormState, setEmailFormState] = useState<Record<string, any>>({
    emailType: EmailType.General,
    senderEmail: '',
    message: '',
    isRecaptchaPassed: false
  })
  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const handleEmailTypeChange = (event: SelectChangeEvent): void => {
    setEmailFormState(prevState => ({
      ...prevState,
      emailType: event.target.value
    }))
  }

  const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.value.length > inputCharLimit) {
      return
    }

    const { id, value } = event.target
    setEmailFormState(prevState => ({
      ...prevState,
      [id]: value
    }))
  }

  const isSubmissionInvalid = (state: Record<string, string>): boolean => {
    return !state.message || !state.isRecaptchaPassed
  }

  const preparePayload = (): GenericEmailPayload => ({
    email_request: {
      email_type: emailFormState.emailType,
      ...(emailFormState.senderEmail && { sender_email: emailFormState.senderEmail }),
      message: emailFormState.message
    }
  })

  const sendMessage = useMutation({
    mutationFn: async (payload: GenericEmailPayload) => {
      return await sendEmail(payload)
    },
    onSuccess: () => {
      setEmailFormState({
        emailType: EmailType.General,
        senderEmail: '',
        message: '',
        isRecaptchaPassed: false
      })
      recaptchaRef.current.reset()
      toast.info('Email sent successfully! Thank you for reaching out.', { autoClose: 2500 })
      setTimeout(() => sendMessage.reset(), 1000)
    },
    onError: (error) => {
      console.error('Error submitting error report:', error)
      toast.error('Could not send email. Please try again later.')
    }
  })


  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    sendMessage.mutate(preparePayload())
  }

  const handleOnChangeReCAPTCHA = (): void => {
    const reValue: string = recaptchaRef.current.getValue()

    setEmailFormState(prevState => ({
      ...prevState,
      isRecaptchaPassed: !!reValue
    }))
  }

  return (
    <>
    <SEOHelmet
      {...DEFAULT_SEO.CONTACT_US}
      canonicalUrl={generateCanonicalUrl(ROUTES.CONTACT_US)}
    />
    <Box display="flex" flexDirection="column" pb={18}
      sx={{
        backgroundColor: theme.palette.custom.backgroundWhisperPurple,
        minHeight: 'calc(100vh - 151px)',
        paddingTop: { xs: 4, sm: 5, md: 10 }
      }}>
      <Grid container justifyContent="center">
        <Grid item xs={10} sm={10} md={6} lg={4} xl={3}>
          <Typography component="h1" sx={{
            typography: {
              xs: 'h5',
              sm: 'h4',
              md: 'h4',
              lg: 'h3'
            }
          }} gutterBottom>Contact us</Typography>

          <Typography variant="subtitle1" mb={5}>
            Got feedback? Please use the form below to send us a message. We really appreciate your input, criticism and suggestions.
          </Typography>

          <form onSubmit={handleSubmit}>
            <FormControl fullWidth >
              <InputLabel id="contact-reason-label">Reason for contacting us</InputLabel>
              <Select
                labelId="contact-reason-label"
                id="demo-simple-select"
                value={emailFormState.emailType}
                label="Reason for contacting us"
                onChange={handleEmailTypeChange}
                sx={{ backgroundColor: 'white', marginBottom: '16px' }}
              >
                <MenuItem value={EmailType.General}>General message / feedback</MenuItem>
                <MenuItem value={EmailType.ErrorReport}>Report Error</MenuItem>
                <MenuItem value={EmailType.FeatureRequest}>Feature Request</MenuItem>
              </Select>

              <TextField
                margin="dense"
                id="senderEmail"
                value={emailFormState.senderEmail}
                onChange={handleFieldChange}
                label="Your Email"
                type="email"
                variant="outlined"
                sx={{ backgroundColor: 'white', marginBottom: '16px' }}
              />

              <TextField
                margin="dense"
                id="message"
                value={emailFormState.message}
                onChange={handleFieldChange}
                label="Message"
                required
                type="text"
                variant="outlined"
                multiline
                rows={4}
                sx={{ backgroundColor: 'white' }}
              />
              <CharacterCounter text={emailFormState.message} charLimit={inputCharLimit} sx={{ marginBottom: '20px' }} />
              <ReCAPTCHA
                ref={recaptchaRef}
                size="normal"
                sitekey={fetchEnvironmentVar('REACT_APP_GOOGLE_SITE_KEY')}
                onChange={handleOnChangeReCAPTCHA}
                style={{ marginBottom: '30px' }}
              />
              {sendMessage.isPending
                ? <CircularProgress />
                : <Button variant="contained" size="large" color="primary" type="submit"
                    sx={{
                      background: theme.palette.custom.beautifulGradient,
                      alignSelf: 'flex-start'
                    }}
                    disabled={isSubmissionInvalid(emailFormState)}
                  >
                    Submit <FontAwesomeIcon icon={faEnvelope} style={{ marginLeft: '8px', fontSize: '1rem' }} />
                  </Button>
              }
            </FormControl>
          </form>
        </Grid>
      </Grid>
    </Box>
    </>
  )
}


export default ContactUsPage
